import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { doc, getDoc, collection, addDoc, serverTimestamp, Timestamp, query, where, orderBy, limit, getDocs } from 'firebase/firestore';
import { db, auth } from '../../firebase';
import { useTranslation } from 'react-i18next';
import AgreementModal from './AgreementModal';
import { formatInTimeZone } from 'date-fns-tz';
import { formatDateOnly } from '../../lib/dateOnly';

// Fixed timezone for consistent display across all users (US Central Time)
const DISPLAY_TIMEZONE = 'America/Chicago';

// Format discount deadline with consistent timezone
const formatDiscountDate = (date: Date | null | undefined): string => {
    return formatDateOnly(date);
};

// Parse date string as local timezone to avoid timezone conversion issues
// When user inputs 2015-01-15, it should be stored as 2015-01-15 in local timezone, not UTC
const parseDateAsLocalTime = (dateString: string): Date => {
    // Split the date string to get year, month, day
    const [year, month, day] = dateString.split('-').map(Number);
    // Create date using local timezone (month is 0-indexed in JS Date)
    return new Date(year, month - 1, day);
};

// Format date of birth with consistent timezone for display
const formatDisplayDate = (timestamp: any): string => {
    if (!timestamp) return '-';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return formatInTimeZone(date, DISPLAY_TIMEZONE, 'M/d/yyyy');
};

interface RegistrationFormProps {
    programId?: string;
}

interface CourseGroup {
    groupName: string;
    groupNameEn?: string;
    courses: any[]; // 保持简化，实际是EntityReference[]
    singleSelection: boolean;
    required: boolean;
}

interface Program {
    id: string;
    title: string;
    titleEn?: string;
    subtitle?: string;
    managementFee?: number;
    discountDeadline?: Date;
    agreements: any[]; // 修改为any数组，可以存储DocumentReference或字符串
    courseGroups?: CourseGroup[]; // 新版课程分组
}

interface Course {
    id: string;
    name: string;
    nameEn?: string;
    avatar?: string;
    courseContent?: string;
    scheduleInfo?: string;
    status: string;
    price: number;
    earlyBirdPrice: number;
    location?: string;
    selected?: boolean; // 前端状态：是否选中
    groupId?: string; // 标记课程所属分组
}

interface RegistrationFormValues {
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
    address: string;
    city: string;
    province: string;
    postalCode: string;
    country: string;
    program: string;
    agreement: boolean;
    selectedCourses: { [key: string]: string | string[] };
}

// 在RegistrationForm组件定义之前添加美国州名列表
const US_STATES = [
    { code: 'AL', name: 'Alabama' },
    { code: 'AK', name: 'Alaska' },
    { code: 'AZ', name: 'Arizona' },
    { code: 'AR', name: 'Arkansas' },
    { code: 'CA', name: 'California' },
    { code: 'CO', name: 'Colorado' },
    { code: 'CT', name: 'Connecticut' },
    { code: 'DE', name: 'Delaware' },
    { code: 'FL', name: 'Florida' },
    { code: 'GA', name: 'Georgia' },
    { code: 'HI', name: 'Hawaii' },
    { code: 'ID', name: 'Idaho' },
    { code: 'IL', name: 'Illinois' },
    { code: 'IN', name: 'Indiana' },
    { code: 'IA', name: 'Iowa' },
    { code: 'KS', name: 'Kansas' },
    { code: 'KY', name: 'Kentucky' },
    { code: 'LA', name: 'Louisiana' },
    { code: 'ME', name: 'Maine' },
    { code: 'MD', name: 'Maryland' },
    { code: 'MA', name: 'Massachusetts' },
    { code: 'MI', name: 'Michigan' },
    { code: 'MN', name: 'Minnesota' },
    { code: 'MS', name: 'Mississippi' },
    { code: 'MO', name: 'Missouri' },
    { code: 'MT', name: 'Montana' },
    { code: 'NE', name: 'Nebraska' },
    { code: 'NV', name: 'Nevada' },
    { code: 'NH', name: 'New Hampshire' },
    { code: 'NJ', name: 'New Jersey' },
    { code: 'NM', name: 'New Mexico' },
    { code: 'NY', name: 'New York' },
    { code: 'NC', name: 'North Carolina' },
    { code: 'ND', name: 'North Dakota' },
    { code: 'OH', name: 'Ohio' },
    { code: 'OK', name: 'Oklahoma' },
    { code: 'OR', name: 'Oregon' },
    { code: 'PA', name: 'Pennsylvania' },
    { code: 'RI', name: 'Rhode Island' },
    { code: 'SC', name: 'South Carolina' },
    { code: 'SD', name: 'South Dakota' },
    { code: 'TN', name: 'Tennessee' },
    { code: 'TX', name: 'Texas' },
    { code: 'UT', name: 'Utah' },
    { code: 'VT', name: 'Vermont' },
    { code: 'VA', name: 'Virginia' },
    { code: 'WA', name: 'Washington' },
    { code: 'WV', name: 'West Virginia' },
    { code: 'WI', name: 'Wisconsin' },
    { code: 'WY', name: 'Wyoming' },
    { code: 'DC', name: 'District of Columbia' },
    { code: 'AS', name: 'American Samoa' },
    { code: 'GU', name: 'Guam' },
    { code: 'MP', name: 'Northern Mariana Islands' },
    { code: 'PR', name: 'Puerto Rico' },
    { code: 'VI', name: 'U.S. Virgin Islands' }
];

const RegistrationForm: React.FC<RegistrationFormProps> = ({ programId: propProgramId }) => {
    const { t, i18n } = useTranslation();
    const navigate = useNavigate();
    const location = useLocation();

    // 从url中获取项目ID
    const searchParams = new URLSearchParams(location.search);
    const programId = searchParams.get('program') || propProgramId || '';

    const [program, setProgram] = useState<Program | null>(null);
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState(false);

    // 协议确认相关状态
    const [showAgreementModal, setShowAgreementModal] = useState(false);
    const [agreementsConfirmed, setAgreementsConfirmed] = useState(false);
    const [pendingRegistrationData, setPendingRegistrationData] = useState<any>(null);

    // 计算总价
    const [totalPrice, setTotalPrice] = useState<number>(0);
    const [earlyBirdPrice, setEarlyBirdPrice] = useState<number>(0);
    // 是否使用了之前的报名信息
    const [usedPreviousInfo, setUsedPreviousInfo] = useState<boolean>(false);

    // 表单字段
    const [formData, setFormData] = useState({
        // 学生信息
        firstName: '',
        lastName: '',
        chineseName: '',
        gender: '',
        dateOfBirth: '',
        grade: '',
        fullTimeSchoolName: '',
        allergies: '',

        // 家长/监护人信息
        parentFirstName: '',
        parentLastName: '',
        phone: '',
        email: '',
        address: '',
        aptNumber: '',
        city: '',
        state: '',
        zipCode: '',

        // 紧急联系人
        emergencyFirstName: '',
        emergencyLastName: '',
        emergencyPhone: '',
        emergencyEmail: '',
        emergencyAddress: '',
        emergencyAptNumber: '',
        emergencyCity: '',
        emergencyState: '',
        emergencyZipCode: '',

        // 其他信息
        parentMessage: '',
    });

    // 表单验证错误
    const [formErrors, setFormErrors] = useState<Record<string, string>>({});

    // 添加课程组状态
    const [courseGroups, setCourseGroups] = useState<{ [key: string]: Course[] }>({});
    const [selectedCourses, setSelectedCourses] = useState<{ [key: string]: string | string[] }>({});

    // 获取项目和课程信息
    useEffect(() => {
        fetchData();
    }, [programId]);

    const fetchData = async () => {
        try {
            setLoading(true);
            setError(null);

            // 使用Promise等待身份验证状态初始化
            const user = await new Promise<any>((resolve) => {
                const unsubscribe = auth.onAuthStateChanged((user) => {
                    unsubscribe();
                    resolve(user);
                });
            });

            // 检查用户是否登录
            if (!user) {
                setError(t('registration.needLogin'));
                navigate('/login', { state: { from: '/registration' } });
                return;
            } else {
                console.log('user', user, user.email);
                setFormData(prevState => ({
                    ...prevState,
                    email: user.email || ''
                }));
            }

            // 获取项目信息
            if (programId) {
                const programDoc = await getDoc(doc(db, 'programs', programId));
                console.log('programDoc', programDoc.data());
                if (programDoc.exists()) {
                    const programData = programDoc.data();
                    // 确保agreements字段是数组
                    let agreements = programData.agreements || [];

                    // 如果agreements不是数组，转换为数组
                    if (!Array.isArray(agreements)) {
                        console.warn('Program agreements不是数组，进行转换:', agreements);
                        if (typeof agreements === 'string' ||
                            (typeof agreements === 'object' && agreements !== null && agreements.path)) {
                            agreements = [agreements]; // 如果是字符串或引用对象，转为单元素数组
                        } else {
                            agreements = []; // 其他情况设为空数组
                        }
                    }

                    console.log('处理后的agreements:', agreements);
                    console.log('programData', programData);
                    const programObj = {
                        id: programDoc.id,
                        title: programData.title,
                        titleEn: programData.titleEn,
                        subtitle: programData.subtitle,
                        agreements: agreements,
                        courseGroups: programData.courseGroups || [],
                        managementFee: programData.managementFee || 0,
                        discountDeadline: programData.discountDeadline ? new Date(programData.discountDeadline.seconds * 1000) : null
                    };

                    setProgram(programObj);

                    // 处理课程组数据
                    const groups: { [key: string]: Course[] } = {};
                    let totalPriceCalculation = 0;
                    let earlyBirdPriceCalculation = 0;
                    // 检查课程组结构
                    if (programData.courseGroups && Array.isArray(programData.courseGroups)) {
                        for (let i = 0; i < programData.courseGroups.length; i++) {
                            const group = programData.courseGroups[i];
                            const groupId = `group-${i}`;

                            // 如果组中有课程
                            if (group.courses && Array.isArray(group.courses)) {
                                // 使用Promise.all并行获取所有课程
                                const coursesPromises = group.courses.map(async (courseRef) => {
                                    try {
                                        const courseDoc = await getDoc(courseRef);
                                        if (courseDoc.exists()) {
                                            const courseData = courseDoc.data() as any;
                                            if (courseData.status === 'pending' || courseData.status === 'active') {
                                                return {
                                                    id: courseDoc.id,
                                                    name: courseData.name || '',
                                                    nameEn: courseData.nameEn || '',
                                                    avatar: courseData.avatar || '',
                                                    courseContent: courseData.courseContent || '',
                                                    scheduleInfo: courseData.scheduleInfo || '',
                                                    status: courseData.status || 'pending',
                                                    price: courseData.price || 0,
                                                    earlyBirdPrice: courseData.earlyBirdPrice || courseData.price,
                                                    location: courseData.location || '',
                                                    selected: false, // 初始设为false，稍后根据需要更新
                                                    groupId: groupId
                                                };
                                            }
                                        }
                                        return null;
                                    } catch (error) {
                                        console.error('获取课程信息失败:', error);
                                        return null;
                                    }
                                });

                                // 等待所有课程加载完成
                                const coursesResults = await Promise.all(coursesPromises);

                                // 过滤掉null值并处理课程数据
                                const coursesData = coursesResults.filter(course => course !== null) as Course[];

                                if (coursesData.length > 0) {
                                    // 如果是必选组，默认选中第一个课程
                                    if (group.required && coursesData.length > 0) {
                                        coursesData[0].selected = true;
                                        setSelectedCourses(prev => ({ ...prev, [groupId]: coursesData[0].id }));
                                        totalPriceCalculation += coursesData[0].price || 0;
                                        earlyBirdPriceCalculation += coursesData[0].earlyBirdPrice || 0;
                                    }

                                    // 将此组的课程添加到组映射中
                                    groups[groupId] = coursesData;
                                }
                            }
                        }

                        // 设置课程组
                        setCourseGroups(groups);

                        // 设置总价格
                        console.log('totalPriceCalculation', programData);
                        setTotalPrice(totalPriceCalculation + programData.managementFee);
                        setEarlyBirdPrice(earlyBirdPriceCalculation);
                    } else {
                        // 如果没有课程组，则设置为空对象
                        setCourseGroups({});
                    }
                } else {
                    setError(t('registration.programNotFound'));
                }
            } else {
                setError(t('registration.noProgramSelected'));
            }

            // 尝试获取用户之前的报名信息
            await fetchPreviousRegistration(user.uid);

        } catch (error) {
            console.error('Error:', error);
            setError(t('registration.fetchError'));
        } finally {
            setLoading(false);
        }
    };

    // 获取用户之前的报名信息
    const fetchPreviousRegistration = async (userId: string) => {
        try {
            // 查询用户之前的报名记录
            const registrationsRef = collection(db, 'registrations');
            const q = query(
                registrationsRef,
                where('user', '==', doc(db, 'users', userId)),
                orderBy('createdAt', 'desc'),
                limit(1)
            );

            const querySnapshot = await getDocs(q);

            if (!querySnapshot.empty) {
                const previousRegistration = querySnapshot.docs[0].data();

                // 填充表单数据
                setFormData(prevState => ({
                    ...prevState,
                    firstName: previousRegistration.firstName || '',
                    lastName: previousRegistration.lastName || '',
                    chineseName: previousRegistration.chineseName || '',
                    gender: previousRegistration.gender || '',
                    dateOfBirth: previousRegistration.dateOfBirth ? previousRegistration.dateOfBirth.toDate().toISOString().split('T')[0] : '',
                    grade: previousRegistration.grade || '',
                    fullTimeSchoolName: previousRegistration.fullTimeSchoolName || '',
                    allergies: previousRegistration.allergies || '',

                    parentFirstName: previousRegistration.parentFirstName || '',
                    parentLastName: previousRegistration.parentLastName || '',
                    phone: previousRegistration.phone || '',
                    // email: previousRegistration.email || '',
                    address: previousRegistration.address || '',
                    aptNumber: previousRegistration.aptNumber || '',
                    city: previousRegistration.city || '',
                    state: previousRegistration.state || '',
                    zipCode: previousRegistration.zipCode || '',

                    emergencyFirstName: previousRegistration.emergencyFirstName || '',
                    emergencyLastName: previousRegistration.emergencyLastName || '',
                    emergencyPhone: previousRegistration.emergencyPhone || '',
                    emergencyEmail: previousRegistration.emergencyEmail || '',
                    emergencyAddress: previousRegistration.emergencyAddress || '',
                    emergencyAptNumber: previousRegistration.emergencyAptNumber || '',
                    emergencyCity: previousRegistration.emergencyCity || '',
                    emergencyState: previousRegistration.emergencyState || '',
                    emergencyZipCode: previousRegistration.emergencyZipCode || '',
                }));

                setUsedPreviousInfo(true);
            }
        } catch (error) {
            console.error('获取之前的报名信息失败:', error);
            // 这里不设置错误状态，因为这不是关键功能
        }
    };

    // 更新课程选择的处理函数
    const handleCourseChange = (courseId: string, groupId: string) => {
        const groups = { ...courseGroups };
        const group = groups[groupId];

        if (!group) return;

        // 获取组信息
        const groupIndex = parseInt(groupId.replace('group-', ''));
        const groupInfo = program?.courseGroups?.[groupIndex];
        const isSingleSelection = groupInfo?.singleSelection !== false; // 默认为单选
        const isRequired = groupInfo?.required === true;

        // 如果是单选模式
        if (isSingleSelection) {
            // 获取当前要选择的课程
            const courseToSelect = group.find(c => c.id === courseId);
            const isAlreadySelected = courseToSelect?.selected === true;

            // 如果课程已经被选中且不是必选组，则允许取消选择
            if (isAlreadySelected && !isRequired) {
                // 取消当前课程的选中状态
                group.forEach(course => {
                    course.selected = false;
                });

                // 从选中的课程中删除
                setSelectedCourses(prev => {
                    const newSelected = { ...prev };
                    delete newSelected[groupId];
                    return newSelected;
                });
            } else {
                // 取消所有选中状态，然后选中当前课程
                group.forEach(course => {
                    course.selected = course.id === courseId;
                });

                // 更新选中的课程
                setSelectedCourses(prev => ({ ...prev, [groupId]: courseId }));
            }
        }
        // 如果是多选模式
        else {
            // 切换当前课程的选中状态
            const courseIndex = group.findIndex(c => c.id === courseId);
            if (courseIndex >= 0) {
                // 如果当前课程已选中且是组内最后一个选中的课程，并且是必选组，则不允许取消选择
                const isLastSelectedInRequiredGroup =
                    group[courseIndex].selected &&
                    isRequired &&
                    group.filter(c => c.selected).length === 1 &&
                    group[courseIndex].id === courseId;

                if (!isLastSelectedInRequiredGroup) {
                    // 切换选中状态
                    group[courseIndex].selected = !group[courseIndex].selected;

                    // 如果选中，添加到选中的课程中
                    if (group[courseIndex].selected) {
                        // 对于多选组，使用数组存储选中的课程ID
                        setSelectedCourses(prev => {
                            const newSelected = { ...prev };
                            if (!newSelected[groupId]) {
                                newSelected[groupId] = courseId;
                            } else if (typeof newSelected[groupId] === 'string') {
                                newSelected[groupId] = [newSelected[groupId], courseId];
                            } else {
                                newSelected[groupId] = [...newSelected[groupId], courseId];
                            }
                            return newSelected;
                        });
                    }
                    // 如果取消选中，从选中的课程中移除
                    else {
                        setSelectedCourses(prev => {
                            const newSelected = { ...prev };
                            if (typeof newSelected[groupId] === 'string') {
                                // 如果只有一个值且被取消选择，删除整个组
                                delete newSelected[groupId];
                            } else if (Array.isArray(newSelected[groupId])) {
                                const selectedArray = newSelected[groupId] as string[];
                                newSelected[groupId] = selectedArray.filter(id => id !== courseId);
                                // 如果数组为空且不是必选组，删除整个组
                                if ((newSelected[groupId] as string[]).length === 0) {
                                    delete newSelected[groupId];
                                }
                            }
                            return newSelected;
                        });
                    }
                }
            }
        }

        // 更新课程组
        setCourseGroups(groups);

        // 重新计算总价
        let newTotalPrice = 0;
        let newEarlyBirdPrice = 0;
        Object.values(groups).forEach(groupCourses => {
            groupCourses.forEach(course => {
                if (course.selected) {
                    newTotalPrice += course.price;
                    newEarlyBirdPrice += course.earlyBirdPrice;
                }
            });
        });
        setTotalPrice(newTotalPrice + program.managementFee);
        setEarlyBirdPrice(newEarlyBirdPrice);
    };

    // 处理表单字段变更
    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target;
        setFormData(prevState => ({
            ...prevState,
            [name]: value
        }));

        // 清除该字段的错误
        if (formErrors[name]) {
            setFormErrors(prevErrors => {
                const updatedErrors = { ...prevErrors };
                delete updatedErrors[name];
                return updatedErrors;
            });
        }
    };

    // 表单验证
    const validateForm = () => {
        const errors: Record<string, string> = {};

        // 检查是否选择了至少一门课程
        const hasSelectedAnyCourse = Object.values(courseGroups).some(courses =>
            courses.some(course => course.selected)
        );

        if (!hasSelectedAnyCourse) {
            errors.courses = t('registration.noCourseSelected', '请至少选择一门课程');
        } else {
            // 课程选择验证 - 检查所有必选组是否已选
            const requiredGroupsMissing = program?.courseGroups?.some((group, index) => {
                const groupId = `group-${index}`;
                return group.required && !selectedCourses[groupId];
            });

            if (requiredGroupsMissing) {
                errors.courses = t('registration.requiredCoursesNotSelected');
            }
        }

        // 基本字段验证
        if (!formData.firstName) errors.firstName = t('validation.required');
        if (!formData.lastName) errors.lastName = t('validation.required');
        if (!formData.gender) errors.gender = t('validation.required');
        if (!formData.dateOfBirth) errors.dateOfBirth = t('validation.required');

        // 家长信息验证
        if (!formData.parentFirstName) errors.parentFirstName = t('validation.required');
        if (!formData.parentLastName) errors.parentLastName = t('validation.required');
        if (!formData.phone) errors.phone = t('validation.required');
        if (!formData.email) errors.email = t('validation.required');
        if (formData.email && !/^\S+@\S+\.\S+$/.test(formData.email)) {
            errors.email = t('validation.emailInvalid');
        }
        if (!formData.address) errors.address = t('validation.required');
        if (!formData.city) errors.city = t('validation.required');
        if (!formData.state) errors.state = t('validation.required');
        if (!formData.zipCode) errors.zipCode = t('validation.required');

        // 紧急联系人验证
        if (!formData.emergencyFirstName) errors.emergencyFirstName = t('validation.required');
        if (!formData.emergencyLastName) errors.emergencyLastName = t('validation.required');
        if (!formData.emergencyPhone) errors.emergencyPhone = t('validation.required');
        if (!formData.emergencyEmail) errors.emergencyEmail = t('validation.required');
        if (formData.emergencyEmail && !/^\S+@\S+\.\S+$/.test(formData.emergencyEmail)) {
            errors.emergencyEmail = t('validation.emailInvalid');
        }
        if (!formData.emergencyAddress) errors.emergencyAddress = t('validation.required');
        if (!formData.emergencyCity) errors.emergencyCity = t('validation.required');
        if (!formData.emergencyState) errors.emergencyState = t('validation.required');
        if (!formData.emergencyZipCode) errors.emergencyZipCode = t('validation.required');

        setFormErrors(errors);
        return Object.keys(errors).length === 0;
    };

    // 处理表单提交
    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!validateForm()) {
            // 滚动到第一个错误
            const firstError = document.querySelector('[aria-invalid="true"]');
            if (firstError) {
                firstError.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
            return;
        }

        try {
            setSubmitting(true);

            // 收集所有选中的课程
            const selectedCoursesRefs: any[] = [];

            // 遍历所有选中的课程组
            Object.entries(selectedCourses).forEach(([groupId, courseIdOrIds]) => {
                if (typeof courseIdOrIds === 'string') {
                    // 单选情况
                    selectedCoursesRefs.push(doc(db, 'courses', courseIdOrIds));
                } else if (Array.isArray(courseIdOrIds)) {
                    // 多选情况
                    courseIdOrIds.forEach(courseId => {
                        selectedCoursesRefs.push(doc(db, 'courses', courseId));
                    });
                }
            });

            // 拼装报名数据
            const registrationData = {
                // 关联信息
                program: doc(db, 'programs', programId),
                courses: selectedCoursesRefs,
                user: auth.currentUser ? doc(db, 'users', auth.currentUser.uid) : null,

                // 学生信息
                firstName: formData.firstName.trim(),
                lastName: formData.lastName.trim(),
                chineseName: formData.chineseName.trim(),
                gender: formData.gender,
                dateOfBirth: parseDateAsLocalTime(formData.dateOfBirth),
                grade: formData.grade,
                fullTimeSchoolName: formData.fullTimeSchoolName.trim(),
                allergies: formData.allergies.trim(),

                // 家长/监护人信息
                parentFirstName: formData.parentFirstName.trim(),
                parentLastName: formData.parentLastName.trim(),
                phone: formData.phone.trim(),
                email: formData.email.trim(),
                address: formData.address.trim(),
                aptNumber: formData.aptNumber.trim(),
                city: formData.city.trim(),
                state: formData.state.trim(),
                zipCode: formData.zipCode.trim(),

                // 紧急联系人
                emergencyFirstName: formData.emergencyFirstName.trim(),
                emergencyLastName: formData.emergencyLastName.trim(),
                emergencyPhone: formData.emergencyPhone.trim(),
                emergencyEmail: formData.emergencyEmail.trim(),
                emergencyAddress: formData.emergencyAddress.trim(),
                emergencyAptNumber: formData.emergencyAptNumber.trim(),
                emergencyCity: formData.emergencyCity.trim(),
                emergencyState: formData.emergencyState.trim(),
                emergencyZipCode: formData.emergencyZipCode.trim(),

                // 状态信息
                status: 'pending', // 默认为待审核
                createdAt: serverTimestamp(),
                updatedAt: serverTimestamp(),

                // 价格信息
                totalPrice: totalPrice,
                earlyBirdPrice: earlyBirdPrice,

                // 其他信息
                parentMessage: formData.parentMessage.trim(),
            };

            // 存储待处理的注册数据，等待协议确认
            setPendingRegistrationData(registrationData);

            // 打开协议确认模态框
            setShowAgreementModal(true);

        } catch (error) {
            console.error('Error preparing registration data:', error);
            setError(t('registration.submitError'));
            setSubmitting(false);
        }
    };

    // 提交报名数据
    const submitRegistration = async (registrationData: any) => {
        try {
            // 提交报名数据
            const docRef = await addDoc(collection(db, 'registrations'), registrationData);

            // 设置成功状态
            setSuccess(true);

            // 获取所有选中课程的名称
            const selectedCourseNames: string[] = [];

            // 遍历所有选中的课程组，收集课程名称
            Object.entries(courseGroups).forEach(([groupId, courses]) => {
                courses.forEach(course => {
                    if (course.selected) {
                        selectedCourseNames.push(i18n.language === 'zh' ? course.name : (course.nameEn || course.name));
                    }
                });
            });

            // 跳转到成功页面
            navigate(`/registration/success?registrationId=${docRef.id}`);

        } catch (error) {
            console.error('Error submitting registration:', error);
            setError(t('registration.submitError'));
            setSubmitting(false);
        }
    };

    // 协议确认处理
    const handleAgreementsConfirmed = () => {
        setAgreementsConfirmed(true);
        setShowAgreementModal(false);

        // 提交报名数据
        if (pendingRegistrationData) {
            submitRegistration(pendingRegistrationData);
        }
    };

    // 关闭协议模态框
    const handleCloseAgreementModal = () => {
        setShowAgreementModal(false);
        setSubmitting(false);
    };

    if (loading) {
        return (
            <div className="container mx-auto px-4 py-8 mt-[160px]">
                <div className="max-w-3xl mx-auto">
                    <div className="animate-pulse">
                        <div className="h-8 bg-gray-200 rounded w-1/3 mb-6"></div>
                        <div className="h-4 bg-gray-200 rounded w-full mb-6"></div>
                        <div className="h-64 bg-gray-200 rounded mb-6"></div>
                    </div>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="container mx-auto px-4 py-8">
                <div className="max-w-3xl mx-auto">
                    <div className="bg-red-50 border-l-4 border-red-500 p-4 mb-6">
                        <div className="flex">
                            <div className="flex-shrink-0">
                                <svg className="h-5 w-5 text-red-500" viewBox="0 0 20 20" fill="currentColor">
                                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                                </svg>
                            </div>
                            <div className="ml-3">
                                <p className="text-sm text-red-700">{error}</p>
                            </div>
                        </div>
                    </div>
                    <button
                        onClick={() => navigate('/programs')}
                        className="bg-blue-600 hover:bg-blue-700 text-white py-2 px-4 rounded-md transition-colors"
                    >
                        {t('registration.backToPrograms')}
                    </button>
                </div>
            </div>
        );
    }

    if (success) {
        return (
            <div className="container mx-auto px-4 py-8">
                <div className="max-w-3xl mx-auto">
                    <div className="bg-green-50 border-l-4 border-green-500 p-4 mb-6">
                        <div className="flex">
                            <div className="flex-shrink-0">
                                <svg className="h-5 w-5 text-green-500" viewBox="0 0 20 20" fill="currentColor">
                                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                </svg>
                            </div>
                            <div className="ml-3">
                                <p className="text-sm text-green-700">{t('registration.submitSuccess')}</p>
                            </div>
                        </div>
                    </div>
                    <p className="text-gray-600 mb-4">{t('registration.redirecting')}</p>
                    <div className="flex justify-center">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="container mx-auto px-4 py-8 mt-[160px]">
            <div className="max-w-3xl mx-auto">
                <h1 className="text-2xl font-bold mb-6">{t('registration.title')}</h1>

                {/* 预填充提示 */}
                {usedPreviousInfo && (
                    <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-md mb-6 flex items-start">
                        <svg className="w-5 h-5 mr-2 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                        </svg>
                        <div>
                            <p className="font-medium">{t('registration.previousInfoUsed')}</p>
                            <p className="text-sm">{t('registration.checkAndUpdate')}</p>
                        </div>
                    </div>
                )}

                {/* 课程信息 */}
                <div className="bg-blue-50 p-4 rounded-lg mb-6">
                    <p><span className="font-medium">{t('registration.program')}:</span> {i18n.language === 'zh' ? program?.title : program?.subtitle}</p>
                    <p><span className="font-medium">{t('registration.discountDeadline')}:</span> {formatDiscountDate(program?.discountDeadline)}</p>
                </div>

                {/* 课程选择 */}
                <div className="bg-gray-50 p-6 rounded-lg mb-6">
                    <h2 className="text-xl font-semibold mb-4">{t('registration.selectCourses')}</h2>

                    {Object.keys(courseGroups).length === 0 ? (
                        <p className="text-gray-500">{t('registration.noCoursesAvailable')}</p>
                    ) : (
                        <div>
                            {Object.entries(courseGroups).map(([groupId, courses]) => {
                                // 找到对应的组信息
                                const groupIndex = parseInt(groupId.replace('group-', ''));
                                const groupInfo = program?.courseGroups?.[groupIndex];
                                const isDefault = groupId === 'default';

                                return (
                                    <div key={groupId} className="mb-6 last:mb-0">
                                        {/* 显示组标题和描述（非默认组且有组信息时） */}
                                        {!isDefault && groupInfo && (
                                            <div className="mb-3">
                                                <h3 className="text-lg font-medium text-gray-800">{i18n.language === 'zh' ? groupInfo.groupName : (groupInfo.groupNameEn || groupInfo.groupName)}</h3>
                                                {groupInfo.singleSelection && (
                                                    <div className="mt-1 text-sm text-blue-600 flex items-center">
                                                        <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                                                        </svg>
                                                        <span>{t('registration.singleCourseAvailable')}</span>
                                                    </div>
                                                )}
                                                {groupInfo.required && (
                                                    <div className="mt-1 text-sm text-red-600 flex items-center">
                                                        <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"></path>
                                                        </svg>
                                                        <span>{t('registration.required')}</span>
                                                    </div>
                                                )}
                                            </div>
                                        )}

                                        {/* 课程列表 */}
                                        <div className="space-y-3">
                                            {courses.map(course => (
                                                <div
                                                    key={course.id}
                                                    className={`flex items-center p-3 rounded-md border transition-colors cursor-pointer ${course.selected
                                                            ? 'bg-blue-50 border-blue-300'
                                                            : 'bg-white border-gray-200 hover:border-gray-300'
                                                        }`}
                                                    onClick={() => handleCourseChange(course.id, groupId)}
                                                >
                                                    {course.avatar && (
                                                        <img
                                                            src={course.avatar}
                                                            alt={course.name}
                                                            className="w-16 h-16 object-cover rounded-md mr-4"
                                                        />
                                                    )}
                                                    <div className="flex-1">
                                                        <h3 className="font-semibold">{i18n.language === 'zh' ? course.name : (course.nameEn || course.name)}</h3>
                                                        <p className="text-sm text-gray-600">{course.scheduleInfo}</p>
                                                        <p className="text-sm font-medium mt-1">${course.price} {course.earlyBirdPrice != course.price && <span className="text-gray-900">({t('registration.earlyBirdPrice')}:${course.earlyBirdPrice})</span>}</p>
                                                    </div>
                                                    <input
                                                        type="checkbox"
                                                        checked={course.selected}
                                                        readOnly
                                                        className="h-5 w-5 text-blue-600 rounded"
                                                    />
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                );
                            })}

                            {/* 价格信息 */}
                            <div className="mt-6 p-3 bg-blue-50 rounded-md">
                                <div className="flex justify-between items-center">
                                    <div>
                                        <p className="text-sm text-gray-600">{t('registration.selectedCourses')}:
                                            <span className="font-medium"> {
                                                Object.values(courseGroups).reduce((count, courses) => {
                                                    return count + courses.filter(c => c.selected).length;
                                                }, 0)
                                            }</span>
                                        </p>
                                    </div>
                                    <div className="text-right">
                                        <p className="text-lg font-bold text-blue-800">{t('registration.beforeDiscountDate').replace('{{date}}', formatDiscountDate(program?.discountDeadline))}: ${earlyBirdPrice}</p>
                                        <p className="text-lg font-bold text-blue-800">{t('registration.afterDiscountDate').replace('{{date}}', formatDiscountDate(program?.discountDeadline))}: ${totalPrice}</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {formErrors.courses && (
                        <p className="mt-2 text-sm text-red-600">{formErrors.courses}</p>
                    )}
                </div>

                <form onSubmit={handleSubmit} className="space-y-6">
                    {/* 学生信息 */}
                    <div className="bg-gray-50 p-6 rounded-lg">
                        <h2 className="text-xl font-semibold mb-4">{t('registration.studentInfo')}</h2>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {/* 学生姓名 */}
                            <div className="col-span-1">
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    {t('registration.firstName')} <span className="text-red-500">*</span>
                                </label>
                                <input
                                    type="text"
                                    name="firstName"
                                    value={formData.firstName}
                                    onChange={handleChange}
                                    className={`w-full px-3 py-2 border rounded-md ${formErrors.firstName ? 'border-red-500' : 'border-gray-300'}`}
                                    data-error={formErrors.firstName ? 'true' : 'false'}
                                />
                                {formErrors.firstName && (
                                    <p className="mt-1 text-sm text-red-600">{formErrors.firstName}</p>
                                )}
                            </div>

                            {/* 学生姓名 */}
                            <div className="col-span-1">
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    {t('registration.lastName')} <span className="text-red-500">*</span>
                                </label>
                                <input
                                    type="text"
                                    name="lastName"
                                    value={formData.lastName}
                                    onChange={handleChange}
                                    className={`w-full px-3 py-2 border rounded-md ${formErrors.lastName ? 'border-red-500' : 'border-gray-300'}`}
                                    data-error={formErrors.lastName ? 'true' : 'false'}
                                />
                                {formErrors.lastName && (
                                    <p className="mt-1 text-sm text-red-600">{formErrors.lastName}</p>
                                )}
                            </div>

                            {/* 学生姓名 */}
                            <div className="col-span-1">
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    {t('registration.chineseName')}
                                </label>
                                <input
                                    type="text"
                                    name="chineseName"
                                    value={formData.chineseName}
                                    onChange={handleChange}
                                    className={`w-full px-3 py-2 border rounded-md ${formErrors.chineseName ? 'border-red-500' : 'border-gray-300'}`}
                                    data-error={formErrors.chineseName ? 'true' : 'false'}
                                />
                                {formErrors.chineseName && (
                                    <p className="mt-1 text-sm text-red-600">{formErrors.chineseName}</p>
                                )}
                            </div>

                            {/* 性别 */}
                            <div className="col-span-1">
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    {t('registration.gender')} <span className="text-red-500">*</span>
                                </label>
                                <select
                                    name="gender"
                                    value={formData.gender}
                                    onChange={handleChange}
                                    className={`w-full px-3 py-2 border rounded-md ${formErrors.gender ? 'border-red-500' : 'border-gray-300'}`}
                                    data-error={formErrors.gender ? 'true' : 'false'}
                                >
                                    <option value="">{t('registration.selectGender')}</option>
                                    <option value="male">{t('registration.male')}</option>
                                    <option value="female">{t('registration.female')}</option>
                                    <option value="other">{t('registration.other')}</option>
                                </select>
                                {formErrors.gender && (
                                    <p className="mt-1 text-sm text-red-600">{formErrors.gender}</p>
                                )}
                            </div>

                            {/* 出生日期 */}
                            <div className="col-span-1">
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    {t('registration.dateOfBirth')} <span className="text-red-500">*</span>
                                </label>
                                <input
                                    type="date"
                                    name="dateOfBirth"
                                    value={formData.dateOfBirth}
                                    onChange={handleChange}
                                    className={`w-full px-3 py-2 border rounded-md ${formErrors.dateOfBirth ? 'border-red-500' : 'border-gray-300'}`}
                                    data-error={formErrors.dateOfBirth ? 'true' : 'false'}
                                />
                                {formErrors.dateOfBirth && (
                                    <p className="mt-1 text-sm text-red-600">{formErrors.dateOfBirth}</p>
                                )}
                            </div>

                            {/* 年级 */}
                            <div className="col-span-1">
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    {t('registration.grade')} <span className="text-red-500">*</span>
                                </label>
                                <select
                                    name="grade"
                                    value={formData.grade}
                                    onChange={handleChange}
                                    className={`w-full px-3 py-2 border rounded-md ${formErrors.grade ? 'border-red-500' : 'border-gray-300'}`}
                                    data-error={formErrors.grade ? 'true' : 'false'}
                                >
                                    <option value="">{t('registration.selectGrade')}</option>
                                    <option value="kindergarten">{t('registration.grades.kindergarten')}</option>
                                    <option value="grade1">{t('registration.grades.grade1')}</option>
                                    <option value="grade2">{t('registration.grades.grade2')}</option>
                                    <option value="grade3">{t('registration.grades.grade3')}</option>
                                    <option value="grade4">{t('registration.grades.grade4')}</option>
                                    <option value="grade5">{t('registration.grades.grade5')}</option>
                                    <option value="grade6">{t('registration.grades.grade6')}</option>
                                    <option value="grade7">{t('registration.grades.grade7')}</option>
                                    <option value="grade8">{t('registration.grades.grade8')}</option>
                                    <option value="grade9">{t('registration.grades.grade9')}</option>
                                    <option value="grade10">{t('registration.grades.grade10')}</option>
                                    <option value="grade11">{t('registration.grades.grade11')}</option>
                                    <option value="grade12">{t('registration.grades.grade12')}</option>
                                    <option value="other">{t('registration.grades.other')}</option>
                                </select>
                                {formErrors.grade && (
                                    <p className="mt-1 text-sm text-red-600">{formErrors.grade}</p>
                                )}
                            </div>

                            {/* 全职学校名称 */}
                            <div className="col-span-2">
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    {t('registration.fullTimeSchoolName')}
                                </label>
                                <input
                                    type="text"
                                    name="fullTimeSchoolName"
                                    value={formData.fullTimeSchoolName}
                                    onChange={handleChange}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                                    placeholder={t('registration.fullTimeSchoolNamePlaceholder')}
                                />
                            </div>

                            {/* 过敏信息 */}
                            <div className="col-span-2">
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    {t('registration.allergies')}
                                </label>
                                <textarea
                                    name="allergies"
                                    value={formData.allergies}
                                    onChange={handleChange}
                                    rows={3}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                                    placeholder={t('registration.allergiesPlaceholder')}
                                ></textarea>
                            </div>
                        </div>
                    </div>

                    {/* 家长/监护人信息 */}
                    <div className="bg-gray-50 p-6 rounded-lg">
                        <h2 className="text-xl font-semibold mb-4">{t('registration.parentInfo')}</h2>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {/* 家长名字 */}
                            <div className="col-span-1">
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    {t('registration.parentFirstName')} <span className="text-red-500">*</span>
                                </label>
                                <input
                                    type="text"
                                    name="parentFirstName"
                                    value={formData.parentFirstName}
                                    onChange={handleChange}
                                    className={`w-full px-3 py-2 border rounded-md ${formErrors.parentFirstName ? 'border-red-500' : 'border-gray-300'}`}
                                    data-error={formErrors.parentFirstName ? 'true' : 'false'}
                                />
                                {formErrors.parentFirstName && (
                                    <p className="mt-1 text-sm text-red-600">{formErrors.parentFirstName}</p>
                                )}
                            </div>

                            {/* 家长姓 */}
                            <div className="col-span-1">
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    {t('registration.parentLastName')} <span className="text-red-500">*</span>
                                </label>
                                <input
                                    type="text"
                                    name="parentLastName"
                                    value={formData.parentLastName}
                                    onChange={handleChange}
                                    className={`w-full px-3 py-2 border rounded-md ${formErrors.parentLastName ? 'border-red-500' : 'border-gray-300'}`}
                                    data-error={formErrors.parentLastName ? 'true' : 'false'}
                                />
                                {formErrors.parentLastName && (
                                    <p className="mt-1 text-sm text-red-600">{formErrors.parentLastName}</p>
                                )}
                            </div>

                            {/* 电话 */}
                            <div className="col-span-1">
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    {t('registration.phone')} <span className="text-red-500">*</span>
                                </label>
                                <input
                                    type="tel"
                                    name="phone"
                                    value={formData.phone}
                                    onChange={handleChange}
                                    className={`w-full px-3 py-2 border rounded-md ${formErrors.phone ? 'border-red-500' : 'border-gray-300'}`}
                                    data-error={formErrors.phone ? 'true' : 'false'}
                                />
                                {formErrors.phone && (
                                    <p className="mt-1 text-sm text-red-600">{formErrors.phone}</p>
                                )}
                            </div>

                            {/* 邮箱 */}
                            <div className="col-span-1">
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    {t('registration.email')} <span className="text-red-500">*</span>
                                </label>
                                <input
                                    type="email"
                                    name="email"
                                    disabled={true}
                                    value={formData.email}
                                    onChange={handleChange}
                                    className={`w-full px-3 py-2 border rounded-md ${formErrors.email ? 'border-red-500' : 'border-gray-300'}`}
                                    data-error={formErrors.email ? 'true' : 'false'}
                                />
                                {formErrors.email && (
                                    <p className="mt-1 text-sm text-red-600">{formErrors.email}</p>
                                )}
                            </div>

                            {/* 地址 */}
                            <div className="col-span-2">
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    {t('registration.address')} <span className="text-red-500">*</span>
                                </label>
                                <input
                                    type="text"
                                    name="address"
                                    value={formData.address}
                                    onChange={handleChange}
                                    className={`w-full px-3 py-2 border rounded-md ${formErrors.address ? 'border-red-500' : 'border-gray-300'}`}
                                    data-error={formErrors.address ? 'true' : 'false'}
                                    placeholder={t('registration.addressPlaceholder')}
                                />
                                {formErrors.address && (
                                    <p className="mt-1 text-sm text-red-600">{formErrors.address}</p>
                                )}
                            </div>

                            {/* 门牌号 */}
                            <div className="col-span-1">
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    {t('registration.aptNumber')}
                                </label>
                                <input
                                    type="text"
                                    name="aptNumber"
                                    value={formData.aptNumber}
                                    onChange={handleChange}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                                    placeholder={t('registration.aptNumberPlaceholder')}
                                />
                            </div>

                            {/* 城市 */}
                            <div className="col-span-1">
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    {t('registration.city')} <span className="text-red-500">*</span>
                                </label>
                                <input
                                    type="text"
                                    name="city"
                                    value={formData.city}
                                    onChange={handleChange}
                                    className={`w-full px-3 py-2 border rounded-md ${formErrors.city ? 'border-red-500' : 'border-gray-300'}`}
                                    data-error={formErrors.city ? 'true' : 'false'}
                                />
                                {formErrors.city && (
                                    <p className="mt-1 text-sm text-red-600">{formErrors.city}</p>
                                )}
                            </div>

                            {/* 州 */}
                            <div className="col-span-1">
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    {t('registration.state')} <span className="text-red-500">*</span>
                                </label>
                                <select
                                    name="state"
                                    value={formData.state}
                                    onChange={handleChange}
                                    className={`w-full px-3 py-2 border rounded-md ${formErrors.state ? 'border-red-500' : 'border-gray-300'}`}
                                    data-error={formErrors.state ? 'true' : 'false'}
                                >
                                    <option value="">{t('common.select')}</option>
                                    {US_STATES.map(state => (
                                        <option key={state.code} value={state.code}>
                                            {state.code} - {state.name}
                                        </option>
                                    ))}
                                </select>
                                {formErrors.state && (
                                    <p className="mt-1 text-sm text-red-600">{formErrors.state}</p>
                                )}
                            </div>

                            {/* 邮编 */}
                            <div className="col-span-1">
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    {t('registration.zipCode')} <span className="text-red-500">*</span>
                                </label>
                                <input
                                    type="text"
                                    name="zipCode"
                                    value={formData.zipCode}
                                    onChange={handleChange}
                                    className={`w-full px-3 py-2 border rounded-md ${formErrors.zipCode ? 'border-red-500' : 'border-gray-300'}`}
                                    data-error={formErrors.zipCode ? 'true' : 'false'}
                                />
                                {formErrors.zipCode && (
                                    <p className="mt-1 text-sm text-red-600">{formErrors.zipCode}</p>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* 紧急联系人信息 */}
                    <div className="bg-gray-50 p-6 rounded-lg">
                        <h2 className="text-xl font-semibold mb-4">{t('registration.emergencyContact')}</h2>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {/* 紧急联系人名字 */}
                            <div className="col-span-1">
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    {t('registration.emergencyFirstName')} <span className="text-red-500">*</span>
                                </label>
                                <input
                                    type="text"
                                    name="emergencyFirstName"
                                    value={formData.emergencyFirstName}
                                    onChange={handleChange}
                                    className={`w-full px-3 py-2 border rounded-md ${formErrors.emergencyFirstName ? 'border-red-500' : 'border-gray-300'}`}
                                    data-error={formErrors.emergencyFirstName ? 'true' : 'false'}
                                />
                                {formErrors.emergencyFirstName && (
                                    <p className="mt-1 text-sm text-red-600">{formErrors.emergencyFirstName}</p>
                                )}
                            </div>

                            {/* 紧急联系人姓 */}
                            <div className="col-span-1">
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    {t('registration.emergencyLastName')} <span className="text-red-500">*</span>
                                </label>
                                <input
                                    type="text"
                                    name="emergencyLastName"
                                    value={formData.emergencyLastName}
                                    onChange={handleChange}
                                    className={`w-full px-3 py-2 border rounded-md ${formErrors.emergencyLastName ? 'border-red-500' : 'border-gray-300'}`}
                                    data-error={formErrors.emergencyLastName ? 'true' : 'false'}
                                />
                                {formErrors.emergencyLastName && (
                                    <p className="mt-1 text-sm text-red-600">{formErrors.emergencyLastName}</p>
                                )}
                            </div>

                            {/* 紧急联系人电话 */}
                            <div className="col-span-1">
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    {t('registration.emergencyPhone')} <span className="text-red-500">*</span>
                                </label>
                                <input
                                    type="tel"
                                    name="emergencyPhone"
                                    value={formData.emergencyPhone}
                                    onChange={handleChange}
                                    className={`w-full px-3 py-2 border rounded-md ${formErrors.emergencyPhone ? 'border-red-500' : 'border-gray-300'}`}
                                    data-error={formErrors.emergencyPhone ? 'true' : 'false'}
                                />
                                {formErrors.emergencyPhone && (
                                    <p className="mt-1 text-sm text-red-600">{formErrors.emergencyPhone}</p>
                                )}
                            </div>

                            {/* 紧急联系人邮箱 */}
                            <div className="col-span-1">
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    {t('registration.emergencyEmail')} <span className="text-red-500">*</span>
                                </label>
                                <input
                                    type="email"
                                    name="emergencyEmail"
                                    value={formData.emergencyEmail}
                                    onChange={handleChange}
                                    className={`w-full px-3 py-2 border rounded-md ${formErrors.emergencyEmail ? 'border-red-500' : 'border-gray-300'}`}
                                    data-error={formErrors.emergencyEmail ? 'true' : 'false'}
                                />
                                {formErrors.emergencyEmail && (
                                    <p className="mt-1 text-sm text-red-600">{formErrors.emergencyEmail}</p>
                                )}
                            </div>

                            {/* 紧急联系人地址 */}
                            <div className="col-span-2">
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    {t('registration.emergencyAddress')} <span className="text-red-500">*</span>
                                </label>
                                <input
                                    type="text"
                                    name="emergencyAddress"
                                    value={formData.emergencyAddress}
                                    onChange={handleChange}
                                    className={`w-full px-3 py-2 border rounded-md ${formErrors.emergencyAddress ? 'border-red-500' : 'border-gray-300'}`}
                                    data-error={formErrors.emergencyAddress ? 'true' : 'false'}
                                    placeholder={t('registration.emergencyAddressPlaceholder')}
                                />
                                {formErrors.emergencyAddress && (
                                    <p className="mt-1 text-sm text-red-600">{formErrors.emergencyAddress}</p>
                                )}
                            </div>

                            {/* 紧急联系人门牌号 */}
                            <div className="col-span-1">
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    {t('registration.emergencyAptNumber')}
                                </label>
                                <input
                                    type="text"
                                    name="emergencyAptNumber"
                                    value={formData.emergencyAptNumber}
                                    onChange={handleChange}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                                    placeholder={t('registration.emergencyAptNumberPlaceholder')}
                                />
                            </div>

                            {/* 紧急联系人城市 */}
                            <div className="col-span-1">
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    {t('registration.emergencyCity')}<span className="text-red-500">*</span>
                                </label>
                                <input
                                    type="text"
                                    name="emergencyCity"
                                    value={formData.emergencyCity}
                                    onChange={handleChange}
                                    className={`w-full px-3 py-2 border rounded-md ${formErrors.emergencyCity ? 'border-red-500' : 'border-gray-300'}`}
                                    data-error={formErrors.emergencyCity ? 'true' : 'false'}
                                />
                                {formErrors.emergencyCity && (
                                    <p className="mt-1 text-sm text-red-600">{formErrors.emergencyCity}</p>
                                )}
                            </div>

                            {/* 紧急联系人州 */}
                            <div className="col-span-1">
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    {t('registration.emergencyState')}<span className="text-red-500">*</span>
                                </label>
                                <select
                                    name="emergencyState"
                                    value={formData.emergencyState}
                                    onChange={handleChange}
                                    className={`w-full px-3 py-2 border rounded-md ${formErrors.emergencyState ? 'border-red-500' : 'border-gray-300'}`}
                                    data-error={formErrors.emergencyState ? 'true' : 'false'}
                                >
                                    <option value="">{t('common.select')}</option>
                                    {US_STATES.map(state => (
                                        <option key={state.code} value={state.code}>
                                            {state.code} - {state.name}
                                        </option>
                                    ))}
                                </select>
                                {formErrors.emergencyState && (
                                    <p className="mt-1 text-sm text-red-600">{formErrors.emergencyState}</p>
                                )}
                            </div>

                            {/* 紧急联系人邮编 */}
                            <div className="col-span-1">
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    {t('registration.emergencyZipCode')}<span className="text-red-500">*</span>
                                </label>
                                <input
                                    type="text"
                                    name="emergencyZipCode"
                                    value={formData.emergencyZipCode}
                                    onChange={handleChange}
                                    className={`w-full px-3 py-2 border rounded-md ${formErrors.emergencyZipCode ? 'border-red-500' : 'border-gray-300'}`}
                                    data-error={formErrors.emergencyZipCode ? 'true' : 'false'}
                                />
                                {formErrors.emergencyZipCode && (
                                    <p className="mt-1 text-sm text-red-600">{formErrors.emergencyZipCode}</p>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* 其他信息 */}
                    <div className="bg-gray-50 p-6 rounded-lg">
                        <h2 className="text-xl font-semibold mb-4">{t('registration.additionalInfo')}</h2>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                {t('registration.parentMessage')}
                            </label>
                            <textarea
                                name="parentMessage"
                                value={formData.parentMessage}
                                onChange={handleChange}
                                rows={4}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md"
                                placeholder={t('registration.parentMessagePlaceholder')}
                            ></textarea>
                        </div>
                    </div>

                    {/* 提交按钮 */}
                    <div className="flex justify-between items-center">
                        <button
                            type="button"
                            onClick={() => navigate(`/program/${programId}`)}
                            className="bg-gray-200 hover:bg-gray-300 text-gray-800 py-2 px-4 rounded-md transition-colors"
                        >
                            {t('registration.cancel')}
                        </button>

                        <button
                            type="submit"
                            disabled={submitting}
                            className={`bg-blue-600 hover:bg-blue-700 text-white py-2 px-6 rounded-md transition-colors ${submitting ? 'opacity-70 cursor-not-allowed' : ''}`}
                        >
                            {submitting ? (
                                <span className="flex items-center">
                                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                    </svg>
                                    {t('registration.submitting')}
                                </span>
                            ) : t('registration.submit')}
                        </button>
                    </div>
                </form>
            </div>

            {/* 协议确认对话框 */}
            <AgreementModal
                isOpen={showAgreementModal}
                agreements={program?.agreements || []}
                onClose={handleCloseAgreementModal}
                onConfirmAll={handleAgreementsConfirmed}
            />
        </div>
    );
};

export default RegistrationForm;
